import type {
  ActivationCompletionSource,
  ActivationConfettiOutcome,
  ActivationTaskId,
  SetupWizardStepId,
} from '../redux/ownerActivationSlice';

/**
 * Provider-independent analytics contract for the owner activation funnel.
 * See specs/001-new-owner-setup-wizard/contracts/analytics.md.
 *
 * Shared between the native adapter (ownerActivationAnalytics.ts) and the
 * web/Electron adapter (ownerActivationAnalytics.web.ts).
 */

export type OwnerActivationAnalyticsPlatform =
  | 'android'
  | 'ios'
  | 'web'
  | 'electron';

export type OwnerActivationAnalyticsContext = {
  onboarding_version: 1;
  platform: OwnerActivationAnalyticsPlatform;
  business_type_group?: string;
  entry_mode?: 'initial' | 'resume' | 'replay';
  location_type?: 'real' | 'test';
};

export type OwnerSetupEligibleProperties = OwnerActivationAnalyticsContext & {
  source: 'business_setup_completion';
};

export type OwnerSetupLifecycleProperties = OwnerActivationAnalyticsContext & {
  current_step_id?: SetupWizardStepId;
};

export type OwnerSetupStepProperties = OwnerActivationAnalyticsContext & {
  step_id: SetupWizardStepId;
  step_index: number;
};

export type OwnerSetupSkippedProperties = OwnerSetupStepProperties & {
  skip_from_step_id: Exclude<SetupWizardStepId, 'setup_progress'>;
};

export type OwnerSetupProductProperties = OwnerSetupStepProperties & {
  product_mode: 'simple' | 'variants';
  product_source?: 'created' | 'existing';
};

export type OwnerSetupProductCreatedProperties = OwnerSetupProductProperties & {
  refresh_status: 'succeeded' | 'failed';
  completion_source: 'wizard_product_create';
};

export type OwnerSetupProductErrorCategory =
  | 'network'
  | 'validation'
  | 'duplicate'
  | 'authentication'
  | 'location_unavailable'
  | 'server'
  | 'unknown';

export type OwnerSetupProductFailedProperties = OwnerSetupProductProperties & {
  error_category: OwnerSetupProductErrorCategory;
};

export type OwnerSetupDurationBucket =
  | 'under_1m'
  | '1_to_2m'
  | '2_to_5m'
  | 'over_5m';

export type OwnerSetupCompletedProperties = OwnerActivationAnalyticsContext & {
  product_created_during_wizard: boolean;
  duration_bucket: OwnerSetupDurationBucket;
};

export type WizardHomeCelebrationExitReason = 'completed' | 'skipped';

export type WizardHomeCelebrationCtaId =
  | 'primary_start_selling'
  | 'primary_add_product'
  | 'secondary_explore_later'
  | 'dismiss';

export type WizardHomeCelebrationShownProperties =
  OwnerActivationAnalyticsContext & {
    wizard_exit_reason: WizardHomeCelebrationExitReason;
    reduced_motion: boolean;
    product_created_during_wizard: boolean;
  };

export type WizardHomeCelebrationDismissedProperties =
  WizardHomeCelebrationShownProperties & {
    cta_id: WizardHomeCelebrationCtaId;
  };

export type ActivationTrackerProperties = OwnerActivationAnalyticsContext & {
  tracker_phase: 'active' | 'completed_unacknowledged' | 'completed_acknowledged';
  completed_task_count: number;
  wizard_product_created: boolean;
};

export type ActivationTaskProperties = ActivationTrackerProperties & {
  task_id: ActivationTaskId;
};

export type ActivationTaskCompletedProperties = ActivationTaskProperties & {
  completion_source: ActivationCompletionSource;
};

export type ActivationSalePracticeProperties = ActivationTrackerProperties & {
  location_type: 'test';
  sale_state: 'completed';
};

export type ActivationConfettiProperties = ActivationTrackerProperties & {
  confetti_outcome: ActivationConfettiOutcome;
};

export type OwnerActivationAnalyticsEventMap = {
  owner_setup_eligible: OwnerSetupEligibleProperties;
  owner_setup_started: OwnerSetupLifecycleProperties;
  owner_setup_step_viewed: OwnerSetupStepProperties;
  owner_setup_step_completed: OwnerSetupStepProperties;
  owner_setup_skipped: OwnerSetupSkippedProperties;
  owner_setup_skip_cancelled: OwnerSetupStepProperties;
  owner_setup_resumed: OwnerSetupLifecycleProperties;
  owner_setup_reopened: OwnerSetupLifecycleProperties;
  owner_setup_product_started: OwnerSetupProductProperties;
  owner_setup_product_more_options_opened: OwnerSetupProductProperties;
  owner_setup_product_submit_attempted: OwnerSetupProductProperties;
  owner_setup_product_created: OwnerSetupProductCreatedProperties;
  owner_setup_product_failed: OwnerSetupProductFailedProperties;
  owner_setup_product_skipped: OwnerSetupStepProperties;
  owner_setup_existing_product_detected: OwnerSetupProductProperties;
  owner_setup_completed: OwnerSetupCompletedProperties;
  activation_tracker_viewed: ActivationTrackerProperties;
  activation_tracker_expanded: ActivationTrackerProperties;
  activation_tracker_collapsed: ActivationTrackerProperties;
  activation_task_clicked: ActivationTaskProperties;
  activation_task_completed: ActivationTaskCompletedProperties;
  activation_task_bootstrapped: ActivationTaskCompletedProperties;
  activation_sale_practice_completed: ActivationSalePracticeProperties;
  activation_all_completed: ActivationTrackerProperties;
  activation_confetti_outcome: ActivationConfettiProperties;
  activation_completion_acknowledged: ActivationTrackerProperties;
  wizard_home_celebration_shown: WizardHomeCelebrationShownProperties;
  wizard_home_celebration_dismissed: WizardHomeCelebrationDismissedProperties;
};

export type OwnerActivationAnalyticsEventName =
  keyof OwnerActivationAnalyticsEventMap;

export interface OwnerActivationAnalytics {
  track<E extends OwnerActivationAnalyticsEventName>(
    event: E,
    properties: OwnerActivationAnalyticsEventMap[E],
  ): void;
  /**
   * Whether the configured provider actually delivers events remotely.
   * Launch validation reports this honestly per platform (native Firebase
   * delivers; web/Electron currently do not).
   */
  readonly providerDeliversRemotely: boolean;
}

export type AnalyticsProvider = {
  logEvent(name: string, properties: Record<string, unknown>): unknown;
};

/** Event names known to the contract; anything else is rejected. */
export const OWNER_ACTIVATION_EVENT_NAMES: readonly OwnerActivationAnalyticsEventName[] =
  [
    'owner_setup_eligible',
    'owner_setup_started',
    'owner_setup_step_viewed',
    'owner_setup_step_completed',
    'owner_setup_skipped',
    'owner_setup_skip_cancelled',
    'owner_setup_resumed',
    'owner_setup_reopened',
    'owner_setup_product_started',
    'owner_setup_product_more_options_opened',
    'owner_setup_product_submit_attempted',
    'owner_setup_product_created',
    'owner_setup_product_failed',
    'owner_setup_product_skipped',
    'owner_setup_existing_product_detected',
    'owner_setup_completed',
    'activation_tracker_viewed',
    'activation_tracker_expanded',
    'activation_tracker_collapsed',
    'activation_task_clicked',
    'activation_task_completed',
    'activation_task_bootstrapped',
    'activation_sale_practice_completed',
    'activation_all_completed',
    'activation_confetti_outcome',
    'activation_completion_acknowledged',
    'wizard_home_celebration_shown',
    'wizard_home_celebration_dismissed',
  ];

/**
 * Allow-list of property keys per the analytics contract. The sanitizer
 * strips everything else so forbidden business data (names, prices, IDs,
 * tokens, free text) can never reach a provider even if a caller passes it.
 */
export const OWNER_ACTIVATION_ALLOWED_PROPERTY_KEYS: ReadonlySet<string> =
  new Set([
    'onboarding_version',
    'platform',
    'business_type_group',
    'entry_mode',
    'location_type',
    'source',
    'current_step_id',
    'step_id',
    'step_index',
    'skip_from_step_id',
    'product_mode',
    'product_source',
    'refresh_status',
    'completion_source',
    'error_category',
    'product_created_during_wizard',
    'duration_bucket',
    'tracker_phase',
    'completed_task_count',
    'wizard_product_created',
    'task_id',
    'confetti_outcome',
    'sale_state',
    'wizard_exit_reason',
    'reduced_motion',
    'cta_id',
  ]);

/** Coarse wizard duration bucket; precise durations stay off the wire. */
export function toOwnerSetupDurationBucket(
  durationMs: number,
): OwnerSetupDurationBucket {
  if (!Number.isFinite(durationMs) || durationMs < 0) return 'over_5m';
  if (durationMs < 60_000) return 'under_1m';
  if (durationMs < 120_000) return '1_to_2m';
  if (durationMs < 300_000) return '2_to_5m';
  return 'over_5m';
}
